import React, { BaseSyntheticEvent } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { Button } from '@demandlocal/components'

import { ReactComponent as EditIcon } from '../../../images/icons/action_edit.svg'
import { useEditUser } from '../../../api/user/useEditUser'
import toast from 'react-hot-toast'
import Modal from '../../../components/Modal/Modal'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import TabMenu from '../../../components/TabMenu/TabMenu'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../store/selectors/userSelector'
import { useAuthVerifyTwoFactorToken } from '../../../api/auth/useAuthVerifyTwoFactorToken'
import EditUserProfileInfo from '../../../forms/User/EditUserProfile'
import EditUserTwoFactor from '../../../forms/User/EditUserTwoFactor'
import { queryClient } from '../../../api/queryClient'

type Props = {
  id?: number | string
}
type FormValues = {
  totp_code: string
  totp_type: string
  phone?: string
  new_password: string
  confirm_password: string
}

export default function EditProfile(): JSX.Element {
  const user = useSelector(currentUserSelector)
  const navigate = useNavigate()
  const formContext = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      ...user,
      phone: user?.phone || '',
      new_password: '',
      confirm_password: '',
    },
  })
  const { setError, handleSubmit, reset } = formContext

  const { mutateAsync: editUser } = useEditUser()
  const { mutateAsync: authVerifyTwoFactorToken } = useAuthVerifyTwoFactorToken()
  const onSubmit = async (
    values: Partial<FieldValues>,
    e: BaseSyntheticEvent<object, any, any> | undefined,
  ): Promise<void> => {
    if (!(e?.nativeEvent instanceof SubmitEvent)) return
    const submitter = e?.nativeEvent?.submitter as HTMLButtonElement
    if (!(submitter instanceof HTMLButtonElement)) return
    if (!user) return
    if (submitter?.name === 'submitMainForm') {
      const toastId = toast.loading('Saving...')
      try {
        if (user?.id) {
          await editUser(
            {
              user_id: user?.id,
              data: {
                ...values,
                role_id: values.role_id?.value,
                organizations: values.organizations?.map(
                  (org: { value: string }) => org.value,
                ),
              },
            },
            {
              onSuccess: () => {
                toast.success('User edited successfully', { id: toastId, duration: 5000 })
                queryClient.refetchQueries(['currentUser'])
                navigate('./../')
              },
              onError: (error: any) => {
                toast.error('Failed to save user edit', { id: toastId, duration: 5000 })
                Object.entries(error?.response?.data ?? []).forEach(([key, value]) => {
                  setError(key as any, { message: value as string })
                })
              },
            },
          )
        } else {
          toast.error('Failed to save user edit', { id: toastId, duration: 5000 })
        }
      } catch (err) {
        toast.error('Failed to save user edit', { id: toastId, duration: 5000 })
      }
    }

    if (submitter?.name === 'submit-2fa-authenticator') {
      const toastId = toast.loading('Verifying code...')
      await authVerifyTwoFactorToken(
        {
          token: values.totp_code,
          type: 'authenticator',
        },
        {
          onSuccess: async () => {
            toast.success('Code verified!', { id: toastId })
            await queryClient.refetchQueries(['user', 'two-factor-methods'])
          },
          onError: () => {
            reset()
            setError('totp_code', { message: 'Invalid code' })
            toast.error('Invalid code!', { id: toastId })
          },
        },
      )
    }
    if (submitter?.name === 'submit-2fa-sms') {
      const toastId = toast.loading('Verifying code...')
      await editUser({
        user_id: user?.id,
        data: {
          phone: values.phone,
        },
      })
      await authVerifyTwoFactorToken(
        {
          phone_number: values.phone,
          token: values.totp_code,
          type: 'sms',
        },
        {
          onSuccess: async () => {
            toast.success('Code verified!', { id: toastId })
            await queryClient.refetchQueries(['user', 'two-factor-methods'])
          },
          onError: () => {
            reset()
            setError('totp_code', { message: 'Invalid code' })
            toast.error('Invalid code!', { id: toastId })
          },
        },
      )
    }
  }
  return (
    <Modal isOpen={true} key={'AdminUserEdit'}>
      <FormProvider {...formContext}>
        <form onSubmit={handleSubmit(onSubmit)} className={'flex flex-1'}>
          <div className={'flex flex-1 flex-col h-screen relative'}>
            <div
              className={
                'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full h-screen'
              }>
              <div className={'flex gap-4 pt-12'}>
                <div
                  className={
                    'w-10 h-10 rounded-full flex items-center justify-center bg-palatanite-blue'
                  }>
                  <EditIcon stroke={'rgba(9, 50, 194, 1)'} />
                </div>
                <span className={'font-bold text-[28px] text-primary-xiketic'}>
                  Edit "{`${user?.first_name} ${user?.last_name}`}"
                </span>
              </div>
              <div className={'mt-12 mb-10'}>
                <TabMenu
                  options={[
                    { name: 'Info', to: 'info' },
                    {
                      name: '2FA',
                      to: 'two-factor',
                    },
                  ]}
                />
              </div>
              <div className={'font-Spartan overflow-scroll h-full'}>
                <Routes>
                  <Route path={'info'} element={<EditUserProfileInfo />} />
                  <Route path={'two-factor'} element={<EditUserTwoFactor />} />
                  <Route path={'*'} element={<Navigate to={'info'} />} />
                </Routes>
              </div>
              <div className={'flex justify-between py-5 border-t-2'}>
                <Button
                  type={'button'}
                  variant={'secondary'}
                  onClick={() => {
                    navigate('../')
                  }}>
                  Cancel
                </Button>
                <Button variant={'primary'} name={'submitMainForm'}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}
